import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { View, Text, Pressable, StyleSheet, TextInput } from '../lib/react-native-compat';
import { useAtom } from 'jotai';
import moment from 'moment';
import xlsx from 'xlsx';
import { loadUserData, loadCostDetailCondensed, loadJobOpenCommitmentsCombined, updateUserDataArray, pNum, fNum } from '../db/company';
import {
    dataSourceJobsAtom, selectedTeamAtom, selectedVersionAtom, dataSourceVersionAtom,
    selectedVersionLockedAtom, userIdAtom, userNameAtom, selectedJobAtom
} from '../store/company';
import { fieldBonusRemoved } from '../helpers/fieldBonus';

// Weekly per-job forecast summary — modeled on the "Helios weekly forecast summary"
// Excel that PMs fill out by hand. Numbers are computed from the same sources the
// Summary tab uses (Day Zero Budget, saved ETC rows, burn tools, cost detail, open
// POs) so they tie out; the narrative/notes are saved per job + version so each
// weekly WIP version keeps its own write-up.

const NOTES_TAB = 'Weekly Job Summary';
const NOTE_CHUNK = 1000; // Forecast_Value column is limited — chunk like AISummary prompts

const NOTE_SECTIONS = [
    { key: 'summary', label: 'Forecast Summary', placeholder: 'Overall gain/fade story for the week — what moved, why, and what is being done about it...' },
    { key: 'labor', label: 'Labor', placeholder: 'Labor over/under notes by discipline or phase...' },
    { key: 'equipment', label: 'Equipment', placeholder: 'Equipment notes...' },
    { key: 'materials', label: 'Materials', placeholder: 'Materials notes — pending POs, CO material, deliveries...' },
    { key: 'rentals', label: 'Rentals', placeholder: 'Rental notes — call-offs needed, rebillable charges, idle rentals...' },
    { key: 'subs', label: 'Subs', placeholder: 'Subcontractor notes — invoicing issues, pending invoices, disputes...' },
    { key: 'other', label: 'Other', placeholder: 'Anything else...' },
];

const FAMILY_ORDER = ['Labor', 'Equipment', 'Rentals', 'Materials', 'Subs', 'Other', 'Contingency'];

// Cost-type code → LEMSO family. Owned equipment (521xx) is split out from
// rentals (rest of 52xxx) to match how the burn tools forecast them.
const familyForCostType = (ct) => {
    const code = (ct || '') + '';
    if (code >= '51000' && code <= '51999') return 'Labor';
    if (code >= '52100' && code <= '52199') return 'Equipment';
    if (code >= '52000' && code <= '52999') return 'Rentals';
    if (code >= '53000' && code <= '53999') return 'Materials';
    if (code >= '54000' && code <= '54999') return 'Subs';
    if (code >= '55000' && code <= '58000') return 'Other';
    return null;
};

// Cost types the Forecast tab sums from approved change orders (bidForecast.js); anything else on a CO row is left out there too
const CO_COST_TYPES = new Set([
    '51100', '51200', '51300', '51400', '51500', '51600', '51700', '51800',
    '52100', '52200', '52300', '52400',
    '53100', '53200', '53300', '53400',
    '54100', '54200', '54300',
    '55100', '55200', '55300', '55400', '55900',
    '56100', '56200', '56300', '56400', '56500', '56600', '56700', '56800',
    '57100', '57200', '57300', '57900',
]);

const money = (v) => {
    if (v == null || isNaN(v) || v === 0) return '-';
    return '$' + fNum(Math.round(v), 1);
};

const SummaryJob = ({ refSubNav }) => {
    const [jobs] = useAtom(dataSourceJobsAtom);
    const [selectedTeam] = useAtom(selectedTeamAtom);
    const [selectedVersion] = useAtom(selectedVersionAtom);
    const [versions] = useAtom(dataSourceVersionAtom);
    const [lockedVersion] = useAtom(selectedVersionLockedAtom);
    const [userId] = useAtom(userIdAtom);
    const [userName] = useAtom(userNameAtom);
    const [globalJob] = useAtom(selectedJobAtom);

    const [selectedJob, setSelectedJob] = useState(null);
    const [dropdownOpen, setDropdownOpen] = useState(false);
    const [jobSearch, setJobSearch] = useState('');
    const [loading, setLoading] = useState(false);
    const [userRows, setUserRows] = useState([]);
    const [costRows, setCostRows] = useState([]);
    const [poRows, setPoRows] = useState([]);
    const [notes, setNotes] = useState({});
    const [savedNotes, setSavedNotes] = useState({});
    const [savingKeys, setSavingKeys] = useState({});
    const [savedAt, setSavedAt] = useState(null);

    const versionInfo = useMemo(() => (versions || []).find(v => v.Version_GUID === selectedVersion) || {}, [versions, selectedVersion]);

    const teamJobs = useMemo(() => {
        const list = (jobs || []).filter(a => a.Cat_Code_BU_27 == selectedTeam).filter(a => a.Cat_Code_BU_08 != 'T&M');
        return list.sort((a, b) => (a.BU_ID + '').localeCompare(b.BU_ID + ''));
    }, [jobs, selectedTeam]);

    const jobInfo = useMemo(() => teamJobs.find(j => j.BU_ID === selectedJob) || {}, [teamJobs, selectedJob]);

    // Preselect the globally selected job (side menu) when it belongs to this team
    useEffect(() => {
        if (!selectedJob && globalJob && teamJobs.some(j => j.BU_ID === globalJob)) {
            setSelectedJob(globalJob);
        }
    }, [globalJob, teamJobs, selectedJob]);

    useEffect(() => {
        if (!selectedJob || !selectedVersion) return;
        let cancelled = false;
        setLoading(true);
        setUserRows([]); setCostRows([]); setPoRows([]);
        Promise.all([
            loadUserData({ id: selectedJob, forecastGuid: selectedVersion }),
            loadCostDetailCondensed({ jobNumber: selectedJob, versionGuid: selectedVersion }),
            // Same call the Forecast tab's Committed Costs come from (loadData.js); readOpenCommitments
            // isn't the version's snapshot and disagreed with it (12426029 subs 216,944 vs 444,444).
            loadJobOpenCommitmentsCombined({ id: selectedJob, versionGuid: selectedVersion }),
        ]).then(([ud, cd, po]) => {
            if (cancelled) return;
            setUserRows(ud?.data || []);
            setCostRows(cd?.data || []);
            setPoRows(po?.data || []);
            setLoading(false);
        }).catch((e) => {
            console.error('[SummaryJob] load failed', e);
            if (!cancelled) setLoading(false);
        });
        return () => { cancelled = true; };
    }, [selectedJob, selectedVersion]);

    // ---- saved notes (chunked, latest-wins — loadUserData already dedups) ----
    useEffect(() => {
        const cells = {};
        userRows.forEach((r) => {
            if (r?.Forecast_Tab === NOTES_TAB) cells[`${r.Forecast_Row_ID}|${r.Forecast_Field_Name}`] = r.Forecast_Value;
        });
        const loaded = {};
        NOTE_SECTIONS.forEach(({ key }) => {
            const count = parseInt(cells[`${key}|chunks`], 10);
            if (!count || count < 1) { loaded[key] = ''; return; }
            let text = '';
            for (let i = 0; i < count; i++) text += cells[`${key}|note_${i}`] || '';
            loaded[key] = text;
        });
        setNotes(loaded);
        setSavedNotes(loaded);
    }, [userRows]);

    const noteRow = useCallback((section, fieldName, value) => ({
        userId: userId || '', userName: userName || '',
        tab: NOTES_TAB, rowId: section, rowLabel: section,
        version: selectedVersion + '', id: selectedJob,
        fieldName, value: value == null ? '' : String(value),
        lockedVersion: lockedVersion, active: 1,
        costType: '', costCode: '', vendor: '', po: '', equipment: '',
    }), [userId, userName, selectedVersion, selectedJob, lockedVersion]);

    const saveNote = useCallback(async (section) => {
        const text = notes[section] == null ? '' : String(notes[section]);
        setSavingKeys(p => ({ ...p, [section]: true }));
        const chunks = [];
        for (let i = 0; i < text.length; i += NOTE_CHUNK) chunks.push(text.slice(i, i + NOTE_CHUNK));
        const rows = [noteRow(section, 'chunks', String(chunks.length))];
        chunks.forEach((c, i) => rows.push(noteRow(section, `note_${i}`, c)));
        try {
            await updateUserDataArray(rows);
            setSavedNotes(p => ({ ...p, [section]: text }));
            setSavedAt(moment().format('h:mm a'));
        } catch (e) {
            console.error('[SummaryJob] note save failed', e);
        }
        setSavingKeys(p => ({ ...p, [section]: false }));
    }, [notes, noteRow]);

    const dirtyKeys = NOTE_SECTIONS.filter(({ key }) => (notes[key] || '') !== (savedNotes[key] || '')).map(s => s.key);

    const saveAllNotes = useCallback(async () => {
        for (const key of dirtyKeys) await saveNote(key);
    }, [dirtyKeys, saveNote]);

    // ---- computed numbers ----
    const calc = useMemo(() => {
        const out = {
            budget: {}, co: {}, spent: {}, etc: {}, openPO: {},
            revenueBudget: 0,
            hoursToDate: 0, hoursRemaining: 0, hoursLastWeek: 0, hoursLastWeekOf: '',
            completionDate: '', startDate: '', physicalComplete: '',
            poDetail: {},
        };
        FAMILY_ORDER.forEach(f => { out.budget[f] = 0; out.co[f] = 0; out.spent[f] = 0; out.etc[f] = 0; out.openPO[f] = 0; out.poDetail[f] = []; });
        if (!selectedJob) return out;

        // --- user data driven pieces (mirrors summary.js so the numbers tie out) ---
        const hideFieldBonus = fieldBonusRemoved(versionInfo?.Version_Start_DateTime);
        const versionStart = versionInfo?.Version_Start_DateTime;
        let completionDate = '';

        userRows.forEach((d) => {
            if (d?.Forecast_Field_Name === 'completionDateForecast' && d?.Forecast_Value) completionDate = moment(d.Forecast_Value).format('M/D/YYYY');
            if (d?.Forecast_Field_Name === 'startDateActual' && d?.Forecast_Value) out.startDate = moment(d.Forecast_Value).format('M/D/YYYY');
            if (d?.Forecast_Field_Name === 'physicalComplete' && d?.Forecast_Value) out.physicalComplete = d.Forecast_Value;
        });
        if (!completionDate && jobInfo?.Planned_Complete_Date) completionDate = moment(jobInfo.Planned_Complete_Date).format('M/D/YYYY');
        out.completionDate = completionDate;

        // No completion date saved → don't cap the window (moment('') is invalid and would zero out the forecast)
        const completionEnd = completionDate ? moment(completionDate).endOf('week') : moment().add(10, 'years');
        const inWindow = (week) => week && moment(week).isAfter(moment(versionStart).startOf('week')) && completionEnd.isAfter(week);
        const phasedEquipment = {};
        const coRows = {};

        userRows.forEach((d) => {
            // Day Zero Budget → budget by family (cost code 101101 = contingency)
            if (d?.Forecast_Tab === 'Day Zero Budget') {
                const ct = (d?.Cost_Type || '') + '';
                const cc = (d?.Cost_Code || '') + '';
                const val = pNum(d?.Value_Amount || 0);
                if (isNaN(val)) return;
                if (cc === '101101') {
                    if (ct[0] === '5') out.budget.Contingency += val;
                } else if (ct[0] === '4') {
                    out.revenueBudget += val;
                } else {
                    const fam = familyForCostType(ct);
                    if (fam) out.budget[fam] += val;
                }
                return;
            }

            // Change Order log rows, one per detail line
            if (d?.Forecast_Tab === 'Change Order') {
                coRows[d?.Forecast_User_Data_GUID || d?.Forecast_Row_ID] = d;
                return;
            }

            // Saved ETC rows (any tab) — same bucketing as summary.js
            if (d?.Cost_Type && d?.Forecast_Field_Name === 'etc' && !(hideFieldBonus && d?.Cost_Type == '51800') && (!d?.Cost_Code || d?.Cost_Code != '101101')) {
                const ct = (d.Cost_Type || '') + '';
                const val = pNum(d?.Value_Amount);
                if (isNaN(val)) return;
                if (ct[0] == '5' && ct[1] == '1' && (ct[2] == '6' || ct[2] == '7' || ct[2] == '8')) out.etc.Labor += val;
                else if (ct[0] == '5' && ct[1] == '2') out.etc.Rentals += val;
                else if (ct[0] == '5' && ct[1] == '3') out.etc.Materials += val;
                else if (ct[0] == '5' && ct[1] == '4') out.etc.Subs += val;
                else if (ct[0] == '5' && (ct[1] == '5' || ct[1] == '6' || ct[1] == '7')) out.etc.Other += val;
                return;
            }

            // Burn Tool Labor (weekly rows: rowId = 'YYYY-MM-DD*...')
            if (d?.Forecast_Tab === 'Burn Tool Labor') {
                const week = d?.Forecast_Row_ID ? d.Forecast_Row_ID.split('*')[0] : '';
                if (inWindow(week)) {
                    const rate = pNum(d.Rate), units = pNum(d.Number_Units), hrs = pNum(d.Number_Hours);
                    if (!isNaN(rate) && !isNaN(units) && !isNaN(hrs)) out.etc.Labor += rate * units * hrs;
                    if (!isNaN(units) && !isNaN(hrs)) out.hoursRemaining += units * hrs;
                }
                return;
            }

            // Burn Tool Labor Phased (weekly rows: rowId = 'phase=YYYY-MM-DD')
            if (d?.Forecast_Tab === 'Burn Tool Labor Phased' && d?.Forecast_Row_ID?.includes('=')) {
                const week = d.Forecast_Row_ID.split('=')[1];
                const h = d?.Number_Hours, bR = d?.Rate, cS = d?.Number_Units;
                if (h && bR && cS && week && moment(week).isAfter(moment(versionStart).startOf('week')) && moment(week).isBefore(completionEnd)) {
                    if (!isNaN(pNum(h)) && !isNaN(pNum(cS))) out.hoursRemaining += pNum(h) * pNum(cS);
                    if (!isNaN(pNum(h)) && !isNaN(pNum(bR)) && !isNaN(pNum(cS))) out.etc.Labor += pNum(h) * pNum(bR) * pNum(cS);
                }
                return;
            }

            // Burn Tool Equipment (weekly rows keyed by date)
            if (d?.Forecast_Tab === 'Burn Tool Equipment') {
                const key = d?.Forecast_Row_ID || '';
                if (key.length == 10 && inWindow(key)) {
                    const rate = pNum(d.Rate), units = pNum(d.Number_Units), hrs = pNum(d.Number_Hours);
                    out.etc.Equipment += (isNaN(rate) ? 0 : rate) * (isNaN(units) ? 0 : units) * (isNaN(hrs) ? 0 : hrs);
                }
                return;
            }

            // Burn Tool Equipment Phased (weekly rows: rowId = 'asset=YYYY-MM-DD', legacy '*'); latest row per id
            if (d?.Forecast_Tab === 'Burn Tool Equipment Phased') {
                phasedEquipment[d?.Forecast_Row_ID] = d;
                return;
            }
        });

        // Approved change orders → the Forecast tab's Approved CO's column (cost code 101101 = contingency)
        Object.values(coRows).forEach((row) => {
            if ((row?.Status || '').toLowerCase() !== 'approved') return;
            const val = pNum(row?.Value_Amount || 0);
            if (isNaN(val)) return;
            const ct = (row?.Cost_Type || '') + '';
            if (row?.Cost_Code == '101101') out.co.Contingency += val;
            else if (CO_COST_TYPES.has(ct)) out.co[familyForCostType(ct)] += val;
        });

        // Priced like summary.js: each week off its own saved Rate, inside the version → completion window
        Object.keys(phasedEquipment).forEach((id) => {
            const r = phasedEquipment[id];
            const sep = id.includes('=') ? '=' : '*';
            const idx = id.lastIndexOf(sep);
            const week = idx >= 0 ? id.substring(idx + 1) : '';
            const rate = pNum(r?.Rate), h = pNum(r?.Number_Hours), n = pNum(r?.Number_Units);
            if (!week || !rate || !h || !n || isNaN(rate) || isNaN(h) || isNaN(n)) return;
            if (moment(week).isAfter(moment(versionStart).startOf('week')) && moment(week).isBefore(completionEnd)) {
                out.etc.Equipment += rate * h * n;
            }
        });

        // Contingency tab — rows carry Current* fields split across records
        const contingRows = {};
        userRows.forEach((d) => {
            if (d?.Forecast_Tab !== 'Contingency') return;
            if (!contingRows[d?.Forecast_Row_ID]) contingRows[d?.Forecast_Row_ID] = {};
            contingRows[d.Forecast_Row_ID][d.Forecast_Field_Name] = d.Forecast_Value;
        });
        Object.values(contingRows).forEach((row) => {
            ['CurrentLabor', 'CurrentEquipment', 'CurrentMaterials', 'CurrentSubs', 'CurrentOther'].forEach((f) => {
                const v = pNum(row[f]);
                if (!isNaN(v)) out.etc.Contingency += v;
            });
        });

        // --- actuals from cost detail (spent + hours) ---
        const weekHours = {};
        costRows.forEach((row) => {
            const code = (row.Cost_Type_Code || '') + '';
            const jc = pNum(row.Job_Cost);
            const fam = familyForCostType(code);
            if (fam && !isNaN(jc)) out.spent[fam] += jc;
            if (code >= '51100' && code <= '51800') {
                const hrs = pNum(row.Total_Hours);
                if (!isNaN(hrs) && hrs) {
                    out.hoursToDate += hrs;
                    if (row.GL_Date) {
                        const wk = moment(row.GL_Date).startOf('week').format('YYYY-MM-DD');
                        weekHours[wk] = (weekHours[wk] || 0) + hrs;
                    }
                }
            }
        });
        const weeks = Object.keys(weekHours).sort();
        if (weeks.length) {
            const lastWeek = weeks[weeks.length - 1];
            out.hoursLastWeek = weekHours[lastWeek];
            out.hoursLastWeekOf = moment(lastWeek).format('M/D');
        }

        // --- open POs by family (remaining committed) ---
        poRows.forEach((row) => {
            const fam = familyForCostType(row?.Cost_Type_Code);
            const open = pNum(row?.PO_True_Open_Amount);
            if (!fam || isNaN(open) || !open) return;
            out.openPO[fam] += open;
            out.poDetail[fam].push({
                vendor: row.Supplier_AB_Name || row.Supplier_Name || 'Unknown Vendor',
                po: row.PO_Number || '',
                open,
            });
        });
        FAMILY_ORDER.forEach((f) => {
            // Collapse PO line items into one row per vendor+PO, largest open first
            const byPO = {};
            out.poDetail[f].forEach((p) => {
                const k = p.vendor + '|' + p.po;
                if (!byPO[k]) byPO[k] = { ...p };
                else byPO[k].open += p.open;
            });
            out.poDetail[f] = Object.values(byPO).sort((a, b) => b.open - a.open);
        });

        return out;
    }, [selectedJob, userRows, costRows, poRows, versionInfo, jobInfo]);

    // EAC = JTD + committed (open POs) + ETC, as on the Forecast tab. Over/(Under) = EAC - current
    // budget (Day Zero + approved COs), so over budget is positive, like the Forecast tab's Delta from Budget EAC.
    const eacFor = (f) => (calc.spent[f] || 0) + (calc.openPO[f] || 0) + (calc.etc[f] || 0);
    const currentFor = (f) => (calc.budget[f] || 0) + (calc.co[f] || 0);
    const totalBudget = FAMILY_ORDER.reduce((s, f) => s + (calc.budget[f] || 0), 0);
    const totalCO = FAMILY_ORDER.reduce((s, f) => s + (calc.co[f] || 0), 0);
    const totalCurrent = totalBudget + totalCO;
    const totalSpent = FAMILY_ORDER.reduce((s, f) => s + (calc.spent[f] || 0), 0);
    const totalCommitted = FAMILY_ORDER.reduce((s, f) => s + (calc.openPO[f] || 0), 0);
    const totalETC = FAMILY_ORDER.reduce((s, f) => s + (calc.etc[f] || 0), 0);
    const totalEAC = totalSpent + totalCommitted + totalETC;
    const costPercent = totalEAC ? Math.round((totalSpent / totalEAC) * 100) : null;

    // ---- export (mirrors the Helios template layout) ----
    const exportToExcel = useCallback(() => {
        if (!selectedJob) return;
        const aoa = [];
        aoa.push(['Weekly Forecast Summary']);
        aoa.push([]);
        aoa.push(['Client Name', 'Project Name', 'Job #', 'Date', '% Complete']);
        aoa.push([jobInfo?.AR_AB_Name || '', jobInfo?.BU_Description || '', selectedJob, moment().format('M/D/YYYY'), calc.physicalComplete || (costPercent != null ? costPercent + '% (cost)' : '')]);
        aoa.push([]);
        aoa.push(['Forecast Summary']);
        aoa.push([notes.summary || '']);
        aoa.push([]);
        aoa.push(['Cost Summary', 'Budget (Day Zero)', "Approved CO's", 'Current Budget', 'Spent', 'Committed', 'ETC', 'EAC', 'Over/(Under)']);
        FAMILY_ORDER.forEach((f) => {
            const eac = eacFor(f);
            aoa.push([f, calc.budget[f] || 0, calc.co[f] || 0, currentFor(f), calc.spent[f] || 0, calc.openPO[f] || 0, calc.etc[f] || 0, eac, eac - currentFor(f)]);
        });
        aoa.push(['Total', totalBudget, totalCO, totalCurrent, totalSpent, totalCommitted, totalETC, totalEAC, totalEAC - totalCurrent]);
        aoa.push([]);
        aoa.push(['Labor Hours', 'Hours', '', '', '', '']);
        aoa.push([`Spent last week${calc.hoursLastWeekOf ? ' (wk of ' + calc.hoursLastWeekOf + ')' : ''}`, Math.round(calc.hoursLastWeek)]);
        aoa.push(['Spent to date', Math.round(calc.hoursToDate)]);
        aoa.push(['Remaining (forecast)', Math.round(calc.hoursRemaining)]);
        NOTE_SECTIONS.filter(s => s.key !== 'summary').forEach(({ key, label }) => {
            aoa.push([]);
            const fam = label === 'Subs' ? 'Subs' : label;
            if (FAMILY_ORDER.includes(fam)) {
                aoa.push([label, 'Spent', 'ETC', 'EAC', 'Open PO', '']);
                aoa.push(['', calc.spent[fam] || 0, calc.etc[fam] || 0, eacFor(fam), calc.openPO[fam] || 0, '']);
            } else {
                aoa.push([label]);
            }
            if (notes[key]) aoa.push([notes[key]]);
        });
        const ws = xlsx.utils.aoa_to_sheet(aoa);
        ws['!cols'] = [{ wch: 34 }, { wch: 18 }, { wch: 14 }, { wch: 16 }, { wch: 14 }, { wch: 14 }, { wch: 14 }, { wch: 14 }, { wch: 14 }];
        const wb = xlsx.utils.book_new();
        xlsx.utils.book_append_sheet(wb, ws, 'Weekly Summary');
        xlsx.writeFile(wb, `Weekly Forecast Summary ${selectedJob} ${moment().format('M-D-YY')}.xlsx`);
    }, [selectedJob, jobInfo, calc, notes, totalBudget, totalCO, totalCurrent, totalSpent, totalCommitted, totalETC, totalEAC, costPercent]);

    if (refSubNav) refSubNav.current = { exportToExcel, createExcel: exportToExcel, setLoadAll: () => { } };

    const filteredDropdownJobs = teamJobs.filter((j) => {
        if (!jobSearch) return true;
        const t = jobSearch.toLowerCase();
        return (j.BU_ID + '').toLowerCase().includes(t) || (j.BU_Description || '').toLowerCase().includes(t) || (j.AR_AB_Name || '').toLowerCase().includes(t);
    });

    const renderNotesBox = (key, placeholder) => (
        <View>
            <TextInput
                multiline
                value={notes[key] || ''}
                onChangeText={(t) => setNotes(p => ({ ...p, [key]: t }))}
                placeholder={placeholder}
                style={styles.notesInput}
            />
            {(notes[key] || '') !== (savedNotes[key] || '') ? (
                <View style={{ flexDirection: 'row', justifyContent: 'flex-end', marginTop: 6 }}>
                    <Pressable style={styles.saveBtn} onPress={() => saveNote(key)} disabled={!!savingKeys[key]}>
                        <Text style={styles.saveBtnText}>{savingKeys[key] ? 'Saving...' : 'Save Notes'}</Text>
                    </Pressable>
                </View>
            ) : null}
        </View>
    );

    const statTile = (label, value, sub) => (
        <View style={styles.statTile} key={label}>
            <Text style={styles.statLabel}>{label}</Text>
            <Text style={styles.statValue}>{value}</Text>
            {sub ? <Text style={styles.statSub}>{sub}</Text> : null}
        </View>
    );

    const sectionCard = (famLabel, noteKey) => {
        const fam = famLabel;
        const eac = eacFor(fam);
        const pos = calc.poDetail[fam] || [];
        return (
            <View style={styles.card} key={fam}>
                <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Text style={styles.cardTitle}>{fam === 'Subs' ? 'Subcontractors' : fam}</Text>
                    <View style={{ flexDirection: 'row', gap: 18 }}>
                        <Text style={styles.miniStat}>Spent <Text style={styles.miniStatVal}>{money(calc.spent[fam])}</Text></Text>
                        <Text style={styles.miniStat}>ETC <Text style={styles.miniStatVal}>{money(calc.etc[fam])}</Text></Text>
                        <Text style={styles.miniStat}>EAC <Text style={styles.miniStatVal}>{money(eac)}</Text></Text>
                        <Text style={styles.miniStat}>Open PO <Text style={styles.miniStatVal}>{money(calc.openPO[fam])}</Text></Text>
                    </View>
                </View>
                {pos.length > 0 ? (
                    <View style={{ marginTop: 10, borderTop: '1px solid #f1f5f9', paddingTop: 8 }}>
                        <Text style={styles.poHeader}>Open POs</Text>
                        {pos.slice(0, 6).map((p, i) => (
                            <View key={i} style={{ flexDirection: 'row', justifyContent: 'space-between', paddingTop: 3, paddingBottom: 3 }}>
                                <Text style={styles.poText}>{p.vendor}{p.po ? '  ·  PO ' + p.po : ''}</Text>
                                <Text style={styles.poAmount}>{money(p.open)}</Text>
                            </View>
                        ))}
                        {pos.length > 6 ? <Text style={styles.poMore}>+ {pos.length - 6} more open POs</Text> : null}
                    </View>
                ) : null}
                <View style={{ marginTop: 10 }}>
                    {renderNotesBox(noteKey, NOTE_SECTIONS.find(s => s.key === noteKey)?.placeholder)}
                </View>
            </View>
        );
    };

    return (
        <View style={styles.page}>
            <View style={styles.headerRow}>
                {/* Job picker */}
                <View style={{ position: 'relative', zIndex: 50 }}>
                    <Pressable style={styles.jobPicker} onPress={() => { setDropdownOpen(!dropdownOpen); setJobSearch(''); }}>
                        <Text style={styles.jobPickerText}>
                            {selectedJob ? `${selectedJob} — ${jobInfo?.BU_Description || ''}` : 'Select a job...'}
                        </Text>
                        <Text style={styles.jobPickerCaret}>{dropdownOpen ? '▲' : '▼'}</Text>
                    </Pressable>
                    {dropdownOpen ? (
                        <View style={styles.dropdown}>
                            <TextInput
                                value={jobSearch}
                                onChangeText={setJobSearch}
                                placeholder="Search job # / name / client..."
                                style={styles.dropdownSearch}
                            />
                            <View style={{ maxHeight: 320, overflowY: 'auto' }}>
                                {filteredDropdownJobs.map((j) => (
                                    <Pressable
                                        key={j.BU_ID}
                                        style={[styles.dropdownItem, j.BU_ID === selectedJob && { backgroundColor: '#eff6ff' }]}
                                        onPress={() => { setSelectedJob(j.BU_ID); setDropdownOpen(false); }}
                                    >
                                        <Text style={styles.dropdownItemTitle}>{j.BU_ID} — {j.BU_Description}</Text>
                                        <Text style={styles.dropdownItemSub}>{j.AR_AB_Name || ''}</Text>
                                    </Pressable>
                                ))}
                                {filteredDropdownJobs.length === 0 ? <Text style={{ padding: 12, color: '#94a3b8', fontSize: 13 }}>No jobs match</Text> : null}
                            </View>
                        </View>
                    ) : null}
                </View>
                <View style={{ flexDirection: 'row', gap: 10, alignItems: 'center' }}>
                    {savedAt ? <Text style={{ color: '#64748b', fontSize: 12 }}>Notes saved {savedAt}</Text> : null}
                    {dirtyKeys.length > 0 ? (
                        <Pressable style={[styles.saveBtn, { paddingTop: 9, paddingBottom: 9 }]} onPress={saveAllNotes}>
                            <Text style={styles.saveBtnText}>Save All Notes ({dirtyKeys.length})</Text>
                        </Pressable>
                    ) : null}
                </View>
            </View>

            {!selectedJob ? (
                <View style={[styles.card, { alignItems: 'center', paddingTop: 60, paddingBottom: 60 }]}>
                    <Text style={{ fontSize: 15, color: '#64748b' }}>Pick a job to build its weekly forecast summary.</Text>
                    <Text style={{ fontSize: 13, color: '#94a3b8', marginTop: 6 }}>Numbers pull from the current forecast; notes save per job and version.</Text>
                </View>
            ) : loading ? (
                <View style={[styles.card, { alignItems: 'center', paddingTop: 60, paddingBottom: 60 }]}>
                    <Text style={{ fontSize: 15, color: '#64748b' }}>Loading {selectedJob}...</Text>
                </View>
            ) : (
                <View>
                    {/* Header strip — mirrors the template's Client / Project / Date / % Complete row */}
                    <View style={[styles.card, { flexDirection: 'row', flexWrap: 'wrap', gap: 28 }]}>
                        {statTile('Client', jobInfo?.AR_AB_Name || '-')}
                        {statTile('Project', jobInfo?.BU_Description || '-', selectedJob)}
                        {statTile('Project Manager', jobInfo?.Project_Manager_Name || '-')}
                        {statTile('Week Of', versionInfo?.Version_Start_DateTime ? moment(versionInfo.Version_Start_DateTime).format('M/D/YYYY') : moment().format('M/D/YYYY'))}
                        {statTile('% Complete', calc.physicalComplete || '-', costPercent != null ? costPercent + '% by cost' : null)}
                        {statTile('Forecast Completion', calc.completionDate || '-')}
                    </View>

                    {/* Narrative */}
                    <View style={styles.card}>
                        <Text style={styles.cardTitle}>Forecast Summary</Text>
                        <View style={{ marginTop: 8 }}>
                            {renderNotesBox('summary', NOTE_SECTIONS[0].placeholder)}
                        </View>
                    </View>

                    {/* Cost table */}
                    <View style={styles.card}>
                        <Text style={styles.cardTitle}>Cost Summary</Text>
                        <View style={[styles.tableRow, { borderBottom: '2px solid #e2e8f0', marginTop: 8 }]}>
                            <Text style={[styles.th, { flex: 1.4, textAlign: 'left' }]}>Category</Text>
                            <Text style={styles.th}>Budget (Day Zero)</Text>
                            <Text style={styles.th}>Approved CO's</Text>
                            <Text style={styles.th}>Current Budget</Text>
                            <Text style={styles.th}>Spent</Text>
                            <Text style={styles.th}>Committed</Text>
                            <Text style={styles.th}>ETC</Text>
                            <Text style={styles.th}>EAC</Text>
                            <Text style={styles.th}>Over/(Under)</Text>
                        </View>
                        {FAMILY_ORDER.map((f) => {
                            const eac = eacFor(f);
                            const ou = eac - currentFor(f);
                            return (
                                <View key={f} style={[styles.tableRow, { borderBottom: '1px solid #f1f5f9' }]}>
                                    <Text style={[styles.td, { flex: 1.4, textAlign: 'left', fontWeight: '500' }]}>{f}</Text>
                                    <Text style={styles.td}>{money(calc.budget[f])}</Text>
                                    <Text style={styles.td}>{money(calc.co[f])}</Text>
                                    <Text style={styles.td}>{money(currentFor(f))}</Text>
                                    <Text style={styles.td}>{money(calc.spent[f])}</Text>
                                    <Text style={styles.td}>{money(calc.openPO[f])}</Text>
                                    <Text style={styles.td}>{money(calc.etc[f])}</Text>
                                    <Text style={styles.td}>{money(eac)}</Text>
                                    <Text style={[styles.td, { color: ou > 0 ? '#dc2626' : ou < 0 ? '#16a34a' : '#0f172a', fontWeight: '500' }]}>{money(ou)}</Text>
                                </View>
                            );
                        })}
                        <View style={[styles.tableRow, { borderTop: '2px solid #e2e8f0' }]}>
                            <Text style={[styles.td, { flex: 1.4, textAlign: 'left', fontWeight: '700' }]}>Total</Text>
                            <Text style={[styles.td, { fontWeight: '700' }]}>{money(totalBudget)}</Text>
                            <Text style={[styles.td, { fontWeight: '700' }]}>{money(totalCO)}</Text>
                            <Text style={[styles.td, { fontWeight: '700' }]}>{money(totalCurrent)}</Text>
                            <Text style={[styles.td, { fontWeight: '700' }]}>{money(totalSpent)}</Text>
                            <Text style={[styles.td, { fontWeight: '700' }]}>{money(totalCommitted)}</Text>
                            <Text style={[styles.td, { fontWeight: '700' }]}>{money(totalETC)}</Text>
                            <Text style={[styles.td, { fontWeight: '700' }]}>{money(totalEAC)}</Text>
                            <Text style={[styles.td, { fontWeight: '700', color: totalEAC - totalCurrent > 0 ? '#dc2626' : '#16a34a' }]}>{money(totalEAC - totalCurrent)}</Text>
                        </View>
                        <Text style={styles.footnote}>Current Budget is the Day Zero Budget plus approved change orders. EAC is spent + committed + ETC, and Over/(Under) is EAC less Current Budget, as in the Forecast tab's Delta from Budget EAC.</Text>
                    </View>

                    {/* Labor hours */}
                    <View style={[styles.card, { flexDirection: 'row', gap: 28 }]}>
                        {statTile(`Hours Last Week${calc.hoursLastWeekOf ? ' (wk of ' + calc.hoursLastWeekOf + ')' : ''}`, calc.hoursLastWeek ? fNum(Math.round(calc.hoursLastWeek), 1) : '-')}
                        {statTile('Hours To Date', calc.hoursToDate ? fNum(Math.round(calc.hoursToDate), 1) : '-')}
                        {statTile('Hours Remaining (Forecast)', calc.hoursRemaining ? fNum(Math.round(calc.hoursRemaining), 1) : '-')}
                    </View>

                    {/* Sections with notes */}
                    {sectionCard('Labor', 'labor')}
                    {sectionCard('Equipment', 'equipment')}
                    {sectionCard('Materials', 'materials')}
                    {sectionCard('Rentals', 'rentals')}
                    {sectionCard('Subs', 'subs')}

                    <View style={styles.card}>
                        <Text style={styles.cardTitle}>Other</Text>
                        <View style={{ flexDirection: 'row', gap: 18, marginTop: 4 }}>
                            <Text style={styles.miniStat}>Spent <Text style={styles.miniStatVal}>{money(calc.spent.Other)}</Text></Text>
                            <Text style={styles.miniStat}>ETC <Text style={styles.miniStatVal}>{money(calc.etc.Other)}</Text></Text>
                            <Text style={styles.miniStat}>Contingency ETC <Text style={styles.miniStatVal}>{money(calc.etc.Contingency)}</Text></Text>
                        </View>
                        <View style={{ marginTop: 10 }}>
                            {renderNotesBox('other', NOTE_SECTIONS.find(s => s.key === 'other')?.placeholder)}
                        </View>
                    </View>
                </View>
            )}
        </View>
    );
};

const styles = StyleSheet.create({
    page: {
        padding: 20,
        maxWidth: 1150,
        backgroundColor: '#F5F7FA',
        minHeight: '100vh',
    },
    headerRow: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        marginBottom: 14,
    },
    jobPicker: {
        flexDirection: 'row',
        alignItems: 'center',
        backgroundColor: '#fff',
        border: '1px solid #cbd5e1',
        borderRadius: 6,
        paddingTop: 9,
        paddingBottom: 9,
        paddingLeft: 14,
        paddingRight: 14,
        minWidth: 340,
        justifyContent: 'space-between',
        boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
    },
    jobPickerText: { fontSize: 14, color: '#0f172a', fontWeight: '500' },
    jobPickerCaret: { fontSize: 10, color: '#64748b', marginLeft: 12 },
    dropdown: {
        position: 'absolute',
        top: 42,
        left: 0,
        width: 440,
        backgroundColor: '#fff',
        border: '1px solid #cbd5e1',
        borderRadius: 8,
        boxShadow: '0 8px 24px rgba(15, 23, 42, 0.12)',
        zIndex: 100,
    },
    dropdownSearch: {
        border: '1px solid #e2e8f0',
        borderRadius: 6,
        margin: 8,
        paddingTop: 7,
        paddingBottom: 7,
        paddingLeft: 10,
        paddingRight: 10,
        fontSize: 13,
    },
    dropdownItem: {
        paddingTop: 8,
        paddingBottom: 8,
        paddingLeft: 12,
        paddingRight: 12,
        borderBottom: '1px solid #f8fafc',
    },
    dropdownItemTitle: { fontSize: 13, color: '#0f172a', fontWeight: '500' },
    dropdownItemSub: { fontSize: 12, color: '#64748b' },
    card: {
        backgroundColor: '#fff',
        border: '1px solid #e2e8f0',
        borderRadius: 8,
        padding: 16,
        marginBottom: 14,
        boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.04)',
    },
    cardTitle: { fontSize: 15, fontWeight: '600', color: '#0f172a' },
    statTile: { minWidth: 130 },
    statLabel: { fontSize: 12, color: '#64748b', marginBottom: 2 },
    statValue: { fontSize: 16, fontWeight: '600', color: '#0f172a' },
    statSub: { fontSize: 12, color: '#94a3b8', marginTop: 2 },
    tableRow: { flexDirection: 'row', alignItems: 'center', paddingTop: 7, paddingBottom: 7 },
    th: { flex: 1, fontSize: 12, color: '#64748b', fontWeight: '600', textAlign: 'right' },
    td: { flex: 1, fontSize: 13, color: '#0f172a', textAlign: 'right' },
    footnote: { fontSize: 11, color: '#94a3b8', marginTop: 8 },
    miniStat: { fontSize: 12, color: '#64748b' },
    miniStatVal: { fontSize: 13, color: '#0f172a', fontWeight: '600' },
    poHeader: { fontSize: 12, color: '#64748b', fontWeight: '600', marginBottom: 2 },
    poText: { fontSize: 12.5, color: '#334155' },
    poAmount: { fontSize: 12.5, color: '#0f172a', fontWeight: '500' },
    poMore: { fontSize: 12, color: '#94a3b8', marginTop: 4 },
    notesInput: {
        border: '1px solid #e2e8f0',
        borderRadius: 6,
        padding: 10,
        fontSize: 13,
        color: '#0f172a',
        minHeight: 74,
        width: '100%',
        fontFamily: 'inherit',
        resize: 'vertical',
        backgroundColor: '#fcfdfe',
    },
    saveBtn: {
        backgroundColor: '#2563eb',
        borderRadius: 6,
        paddingTop: 7,
        paddingBottom: 7,
        paddingLeft: 14,
        paddingRight: 14,
    },
    saveBtnText: { color: '#fff', fontSize: 13, fontWeight: '500' },
});

export default SummaryJob;
